import { useEffect } from 'react'
import { z } from 'zod'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { Contact } from '@/interfaces/contact'
import { listTagsQuery } from '@/queries/tags.query'
import { ContactService } from '@/services/contacts.service'
import { toast } from 'sonner'
import { Button } from '@/components/ui/button'
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form'
import { Input } from '@/components/ui/input'
import {
  Sheet,
  SheetClose,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet'
import { MultiSelect } from '@/components/multi-select'
import { PhoneInput } from '@/components/phone-input'

interface Props {
  open: boolean
  onOpenChange: (open: boolean) => void
  contact?: Contact
}

const formSchema = z.object({
  name: z.string().min(1, 'Nome é obrigatório.'),
  phone: z.string().min(1, 'Telefone é obrigatório.'),
  email: z.string().optional(),
  tagIds: z.array(z.string()),
})
type ContactForm = z.infer<typeof formSchema>

export function ChatContactEditSheet({ open, onOpenChange, contact }: Props) {
  const queryClient = useQueryClient()
  const { data: tagsData, isLoading: isLoadingTags } = useQuery(listTagsQuery)

  const form = useForm<ContactForm>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      name: contact?.name ?? '',
      phone: contact?.phone ? `+${contact.phone}` : '',
      email: contact?.email ?? '',
      tagIds: contact?.tags.map((tag) => String(tag.id)) ?? [],
    },
  })

  useEffect(() => {
    if (contact && open) {
      form.reset({
        name: contact.name ?? '',
        phone: contact.phone ? `+${contact.phone}` : '',
        email: contact.email ?? '',
        tagIds: contact.tags.map((tag) => String(tag.id)) ?? [],
      })
    }
  }, [contact, open, form])

  const onSubmit = form.handleSubmit((data: ContactForm) => {
    if (!contact) return

    const payload = {
      ...data,
      name: data.name.trim(),
      phone: data.phone.replace(/\D/g, ''),
      tagIds: data.tagIds.map(Number),
    }

    toast.promise(ContactService.update(contact.id, payload), {
      loading: 'Salvando contato...',
      success: () => {
        onOpenChange(false)
        form.reset()
        queryClient.invalidateQueries({ queryKey: ['list-desk-tickets'] })
        queryClient.invalidateQueries({ queryKey: ['list-pending-tickets'] })
        queryClient.invalidateQueries({ queryKey: ['list-finished-tickets'] })
        return 'Contato atualizado'
      },
      error: () => {
        return 'Ocorreu um erro ao tentar atualizar o contato.'
      },
    })
  })

  return (
    <Sheet
      open={open}
      onOpenChange={(v) => {
        onOpenChange(v)
        form.reset()
      }}
    >
      <SheetContent className='flex flex-col'>
        <SheetHeader className='text-left'>
          <SheetTitle>Editar Contato</SheetTitle>
          <SheetDescription>
            Atualize o contato provendo as informações necessárias. Clique em
            salvar quando estiver pronto.
          </SheetDescription>
        </SheetHeader>
        <Form {...form}>
          <form
            id='chat-contact-form'
            onSubmit={onSubmit}
            className='flex-1 space-y-5'
          >
            <FormField
              control={form.control}
              name='name'
              render={({ field }) => (
                <FormItem className='space-y-1'>
                  <FormLabel>Nome</FormLabel>
                  <FormControl>
                    <Input
                      {...field}
                      autoFocus
                      placeholder='Digite o nome do contato'
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name='phone'
              render={({ field }) => (
                <FormItem className='space-y-1'>
                  <FormLabel>Telefone</FormLabel>
                  <FormControl>
                    <PhoneInput
                      value={field.value}
                      onChange={field.onChange}
                      defaultCountry='BR'
                      placeholder='Digite o telefone do contato'
                      initialValueFormat='national'
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name='email'
              render={({ field }) => (
                <FormItem className='space-y-1'>
                  <FormLabel>Email</FormLabel>
                  <FormControl>
                    <Input
                      {...field}
                      placeholder='Digite o email do contato'
                      type='email'
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name='tagIds'
              render={({ field }) => (
                <FormItem className='space-y-1'>
                  <FormLabel>Segmentos</FormLabel>
                  <MultiSelect
                    placeholder='Selecione os segmentos'
                    options={(tagsData || []).map((tag) => ({
                      label: tag.name,
                      value: tag.id.toString(),
                    }))}
                    onValueChange={field.onChange}
                    defaultValue={field.value || []}
                    value={field.value || []}
                    disabled={isLoadingTags}
                  />
                  <FormMessage />
                </FormItem>
              )}
            />
          </form>
        </Form>
        <SheetFooter className='gap-2'>
          <SheetClose asChild>
            <Button variant='outline'>Cancelar</Button>
          </SheetClose>
          <Button form='chat-contact-form' type='submit'>
            Atualizar
          </Button>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  )
}
